import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Portal,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { arrayOf, bool, func, shape } from 'prop-types';
import * as R from 'ramda';
import { useWindowScrollPosition } from 'hooks';
import SelectedAuthorizations from './SelectedAuthorizations';

const ActionBar = ({
  authorizationsToActivate,
  authorizationsToDeactivate,
  customerAuthorizations,
  selectedAuthorizations,
  setActivationDialogOpen,
  setConfirmDialogOpen,
  setConfirmDialogType,
  setSelectedAuthorizations,
  showSelectedAuthorizations,
  setShowSelectedAuthorizations,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef(null);
  const selectedAuthorizationsPortalRef = useRef(null);
  const { y } = useWindowScrollPosition();

  const handleMenuItemClick = item => {
    setMenuOpen(!menuOpen);
    if (item === 'deactivate') {
      setConfirmDialogOpen(true);
      setConfirmDialogType(item);
    } else if (item === 'activate') {
      setActivationDialogOpen(true);
    }
  };

  const handleSelectAll = () =>
    R.equals(R.length(selectedAuthorizations), R.length(customerAuthorizations))
      ? setSelectedAuthorizations([])
      : setSelectedAuthorizations(customerAuthorizations);

  const hasSelectedLength = selectedAuthorizations.length > 0;

  const mobileScrollPos = R.gt(y, 68);
  const deskScrollPos = R.gt(y, 12);

  return (
    <>
      <Stack
        alignItems="center"
        bgcolor="graphic.light"
        direction="row"
        justifyContent="space-between"
        sx={{
          mb: { lg: 2 },
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          mx: { xs: mobileScrollPos ? -2 : 0, lg: deskScrollPos ? -2 : 0 },
          position: 'sticky',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          pl: { xs: mobileScrollPos ? 3 : 1, lg: deskScrollPos ? 3 : 1 },
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          pr: { xs: mobileScrollPos ? 4 : 2, lg: deskScrollPos ? 4 : 2 },
          py: 0.5,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          top: { xs: 111, lg: 120 },
          transition: 'margin .1s, padding .1s',
          zIndex: 1,
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasSelectedLength}
                indeterminate={hasSelectedLength}
                onChange={handleSelectAll}
              />
            }
            label=""
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            sx={{ ml: 0, mr: { xs: 1, lg: 2 } }}
          />
          <Chip
            data-testid="chip"
            label={selectedAuthorizations.length}
            sx={theme => ({
              ...theme.typography.caption,
              '.MuiChip-label': {
                padding: '2px 9px',
              },
              backgroundColor: 'secondary.medium',
              color: 'primary.dark',
              fontWeight: 400,
              height: 20,
              lineHeight: '16px',
              marginRight: 1,
            })}
          />

          <Stack direction="column" sx={{ position: 'relative' }}>
            <Button
              disabled={!selectedAuthorizations.length}
              endIcon={
                !!selectedAuthorizations.length && (
                  <ExpandMoreRoundedIcon
                    color="primary"
                    sx={{
                      transform: `rotate(${
                        !lgUp ? '-90' : showSelectedAuthorizations ? '180' : '0'
                      }deg)`,
                    }}
                  />
                )
              }
              onClick={() => setShowSelectedAuthorizations(!!selectedAuthorizations.length)}
              sx={{ padding: theme => theme.spacing('2px', 2, 0) }}
              variant="text"
            >
              <Typography sx={{ lineHeight: '21px', pt: '2px' }} variant="subtitle2">
                {lgUp && `Authorization${selectedAuthorizations.length === 1 ? '' : 's'}`}
                Selected
              </Typography>
            </Button>
            <Box ref={selectedAuthorizationsPortalRef} />
            {lgUp && showSelectedAuthorizations && (
              <ClickAwayListener onClickAway={() => setShowSelectedAuthorizations(false)}>
                <Box>
                  <Portal container={selectedAuthorizationsPortalRef.current}>
                    <Paper
                      sx={{
                        maxHeight: '50vh',
                        minWidth: '30vw',
                        overflowY: 'auto',
                        position: 'absolute',
                      }}
                    >
                      <SelectedAuthorizations
                        selectedAuthorizations={selectedAuthorizations}
                        setSelectedAuthorizations={setSelectedAuthorizations}
                        setShowSelectedAuthorizations={setShowSelectedAuthorizations}
                      />
                    </Paper>
                  </Portal>
                </Box>
              </ClickAwayListener>
            )}
          </Stack>
        </Stack>
        {lgUp ? (
          <Stack direction="row">
            <Button
              disabled={!authorizationsToActivate.length}
              onClick={() => {
                setActivationDialogOpen(true);
              }}
              sx={{ minWidth: 61 }}
              variant="text"
            >
              {authorizationsToActivate.length > 1
                ? `Activate (${authorizationsToActivate.length})`
                : 'Activate'}
            </Button>
            <Button
              disabled={!authorizationsToDeactivate.length}
              onClick={() => {
                setConfirmDialogOpen(true);
                setConfirmDialogType('deactivate');
              }}
              sx={{ minWidth: 61 }}
              variant="text"
            >
              {authorizationsToDeactivate.length > 1
                ? `Deactivate (${authorizationsToDeactivate.length})`
                : 'Deactivate'}
            </Button>
          </Stack>
        ) : (
          <Button
            disabled={!selectedAuthorizations.length}
            endIcon={
              menuOpen ? (
                <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
              ) : (
                <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />
              )
            }
            onClick={() => setMenuOpen(!menuOpen)}
            ref={menuAnchorRef}
            sx={{ padding: theme => theme.spacing('2px', 2, 0) }}
            variant="text"
          >
            Actions
          </Button>
        )}
      </Stack>

      <Menu
        anchorEl={menuAnchorRef?.current}
        onClose={() => setMenuOpen(!menuOpen)}
        open={menuOpen}
      >
        <MenuItem
          disabled={!authorizationsToActivate.length}
          key="activate"
          onClick={() => handleMenuItemClick('activate')}
        >
          {authorizationsToActivate.length > 1
            ? `Activate (${authorizationsToActivate.length})`
            : 'Activate'}
        </MenuItem>
        <MenuItem
          disabled={!authorizationsToDeactivate.length}
          key="deactivate"
          onClick={() => handleMenuItemClick('deactivate')}
        >
          {authorizationsToDeactivate.length > 1
            ? `Deactivate (${authorizationsToDeactivate.length})`
            : 'Deactivate'}
        </MenuItem>
      </Menu>
    </>
  );
};

ActionBar.propTypes = {
  authorizationsToActivate: arrayOf(shape({})).isRequired,
  authorizationsToDeactivate: arrayOf(shape({})).isRequired,
  customerAuthorizations: arrayOf(shape({})).isRequired,
  selectedAuthorizations: arrayOf(shape({})).isRequired,
  setActivationDialogOpen: func.isRequired,
  setConfirmDialogOpen: func.isRequired,
  setConfirmDialogType: func.isRequired,
  setSelectedAuthorizations: func.isRequired,
  setShowSelectedAuthorizations: func.isRequired,
  showSelectedAuthorizations: bool.isRequired,
};

export default ActionBar;
